import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { Bell, LogOut, MoreVertical, Pin, Settings, User } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { logo } from "../../Assests/Images";
import { useLogout } from "../../Hooks/useLogout";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { updateSidebarPreferences } from "../../Store/Reducers/MasterSlice";
import { fetchNotifications } from "../../Store/Reducers/NotificationSlice";
import {
  ROLE_DISPLAY_NAMES,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
  saveToLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import Button from "../Button";
import Icon from "../Icon";
import NotificationPanel from "../NotificationPanel";
import ProfileSettingsModal from "../ProfileSettingsModal";
import {
  SIDEBAR_HOVER_ENABLED_DEFAULT,
  SIDEBAR_NAV_MODE_DEFAULT,
  SIDEBAR_PINNED_DEFAULT,
  SidebarNavMode,
} from "./types";
import "./style.scss";

export interface SidebarMenuItem {
  name: string;
  icon?: string;
  lucideIcon?: React.ComponentType<{ className?: string; size?: number }>;
  path: string;
}

interface SidebarProps {
  menuItems: SidebarMenuItem[];
}

/** Strip optional org slug prefix so menu paths can be matched against the route. */
const getAppPathname = (pathname: string, orgSlug?: string | null): string => {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!orgSlug) return normalized;

  const prefix = `/${orgSlug}`;
  if (normalized.toLowerCase() === prefix.toLowerCase()) {
    return "/";
  }
  if (normalized.toLowerCase().startsWith(`${prefix.toLowerCase()}/`)) {
    return normalized.slice(prefix.length) || "/";
  }
  return normalized;
};

/**
 * Pick the best matching sidebar item for the current app path.
 * Prefers the longest path prefix so nested routes (e.g. /guidance-library/:slug)
 * and deep links from emails highlight the same item as in-app navigation.
 */
const findActiveMenuItem = (
  menuItems: SidebarMenuItem[],
  appPathname: string
): SidebarMenuItem | undefined => {
  const path =
    appPathname.length > 1 && appPathname.endsWith("/")
      ? appPathname.slice(0, -1)
      : appPathname;

  if (path === "/" || path === "") {
    return (
      menuItems.find(item => item.path === "/" || item.path === "/home") ||
      menuItems[0]
    );
  }

  let best: SidebarMenuItem | undefined;
  let bestLen = -1;

  for (const item of menuItems) {
    const itemPath = item.path.startsWith("/") ? item.path : `/${item.path}`;
    const normalizedItem =
      itemPath.length > 1 && itemPath.endsWith("/")
        ? itemPath.slice(0, -1)
        : itemPath;

    const matches =
      path === normalizedItem || path.startsWith(`${normalizedItem}/`);

    if (matches && normalizedItem.length > bestLen) {
      best = item;
      bestLen = normalizedItem.length;
    }
  }

  return best;
};

const Sidebar: React.FC<SidebarProps> = ({ menuItems: icons }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug: routeOrgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();
  const { logoutLoading } = useAppSelector(
    (state: any) => state.authentication
  );
  const { unread } = useAppSelector((state: any) => state.notification);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  // Initialize pin from durable server prefs (login → LOGGED_IN_USER.preferences)
  const getStoredPinState = () => {
    if (window.innerWidth <= 768) return false;
    const pinned = loggedInUser?.preferences?.sidebar_pinned;
    if (pinned === null || pinned === undefined) return SIDEBAR_PINNED_DEFAULT;
    return pinned === true || pinned === "true";
  };

  const [isPinned, setIsPinned] = useState(() => getStoredPinState());
  /** Sidebar expands on hover; pinning makes that expansion sticky. */
  const [isHovering, setIsHovering] = useState(false);
  /** Small tooltip for the pin button only (not the per-icon flyout). */
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  /** Per-icon hover flyout label, used in "flyout" sidebar nav mode. */
  const [flyoutLabel, setFlyoutLabel] = useState<{
    text: string;
    subtitle?: string;
    x: number;
    y: number;
    variant: "default" | "danger" | "profile";
  } | null>(null);
  const profileContainerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const flyoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
    };
  }, []);

  const togglePopover = useCallback(() => setPopoverOpen(prev => !prev), []);

  const showTooltip = (text: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setTooltip({ text, x: rect.right + 8, y: rect.top + rect.height / 2 });
  };

  const hideTooltip = () => setTooltip(null);

  const showFlyoutLabel = (
    text: string,
    el: HTMLElement,
    options?: {
      variant?: "default" | "danger" | "profile";
      subtitle?: string;
    }
  ) => {
    const rect = el.getBoundingClientRect();
    setFlyoutLabel({
      text,
      subtitle: options?.subtitle,
      x: rect.right,
      y: rect.top + rect.height / 2,
      variant: options?.variant || "default",
    });
  };

  const hideFlyoutLabel = () => setFlyoutLabel(null);

  /** Flyout mode: brief delay before showing so sweeping across icons doesn't flicker. */
  const handleFlyoutEnter = (
    text: string,
    el: HTMLElement,
    options?: {
      variant?: "default" | "danger" | "profile";
      subtitle?: string;
    }
  ) => {
    if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
    flyoutTimerRef.current = setTimeout(
      () => showFlyoutLabel(text, el, options),
      120
    );
  };
  const handleFlyoutLeave = () => {
    if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
    hideFlyoutLabel();
  };

  const sidebarNavMode: SidebarNavMode =
    loggedInUser?.preferences?.sidebar_nav_mode || SIDEBAR_NAV_MODE_DEFAULT;
  const hoverEnabled: boolean =
    loggedInUser?.preferences?.sidebar_hover_enabled ??
    SIDEBAR_HOVER_ENABLED_DEFAULT;
  const isPinnedOpen =
    sidebarNavMode === "flyout"
      ? false
      : isPinned || (hoverEnabled && isHovering);
  const fullName = loggedInUser?.fullName || "";
  const organizationName = loggedInUser?.tenantName || "";
  const tenantSlug = loggedInUser?.slug || "";
  const effectiveOrgSlug = routeOrgSlug || tenantSlug || "";
  const userRole = loggedInUser?.role;
  const normalizedRole = normalizeRoleName(userRole);
  const roleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "System Administrator";
  const imageUrl = loggedInUser?.imageUrl || "";

  const activeMenuName = useMemo(() => {
    const appPath = getAppPathname(location.pathname, effectiveOrgSlug);
    return findActiveMenuItem(icons, appPath)?.name || "";
  }, [location.pathname, effectiveOrgSlug, icons]);

  const isNavItemActive = useCallback(
    (element: string) => activeMenuName === element,
    [activeMenuName]
  );

  const renderProfileAvatar = (sizeClass: string) => {
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt="Profile"
          className={`${sizeClass} rounded-full object-cover`}
        />
      );
    }

    return (
      <div
        className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center`}
      >
        <User className="w-4 h-4 text-gray-600" />
      </div>
    );
  };

  const handleNavigation = (item: (typeof icons)[number]) => {
    if (item.path) {
      const normalizedPath = item.path.startsWith("/")
        ? item.path
        : `/${item.path}`;
      const isIzbaAdmin = normalizedRole === "izba_admin";
      const slugForNav = effectiveOrgSlug || tenantSlug;
      const targetPath =
        !isIzbaAdmin && slugForNav
          ? `/${slugForNav}${normalizedPath}`
          : normalizedPath;
      navigate(targetPath);
    }
  };

  const handlePinToggle = () => {
    const newPinnedState = !isPinned;
    const previousPinned = isPinned;
    setIsPinned(newPinnedState);

    if (!newPinnedState) {
      // Unpinning collapses the sidebar immediately, even if still hovering
      setIsHovering(false);
    }

    setTooltip(null);

    const loggedIn = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
    if (loggedIn) {
      saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, {
        ...loggedIn,
        preferences: {
          ...(loggedIn.preferences || {}),
          sidebar_pinned: newPinnedState,
        },
      });
    }

    void dispatch(updateSidebarPreferences({ sidebar_pinned: newPinnedState }))
      .unwrap()
      .catch(() => {
        // Rollback optimistic pin + localStorage on failure
        setIsPinned(previousPinned);
        const current = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
        if (current) {
          saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, {
            ...current,
            preferences: {
              ...(current.preferences || {}),
              sidebar_pinned: previousPinned,
            },
          });
        }
      });
  };

  const handleLogout = useLogout();

  // Fetch notifications on mount and periodically
  useEffect(() => {
    dispatch(fetchNotifications({}));
    const interval = setInterval(() => {
      dispatch(fetchNotifications({}));
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Force-collapse on mobile; restore durable pin when returning to desktop
  useEffect(() => {
    if (isMobile) {
      setIsHovering(false);
      setIsPinned(false);
      return;
    }
    const prefs = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER)?.preferences;
    const pinned = prefs?.sidebar_pinned;
    setIsPinned(
      pinned === null || pinned === undefined
        ? SIDEBAR_PINNED_DEFAULT
        : pinned === true || pinned === "true"
    );
  }, [isMobile]);

  const maxVisible = isMobile ? 4 : icons.length;
  const visibleItems = icons.slice(0, maxVisible);
  const overflowItems = icons.slice(maxVisible);

  const renderNavItem = (item: (typeof icons)[number]) => {
    const active = isNavItemActive(item.name);
    return (
      <div
        key={item.name}
        className="nav-links__container"
        onMouseEnter={
          sidebarNavMode === "flyout"
            ? e => handleFlyoutEnter(item.name, e.currentTarget)
            : undefined
        }
        onMouseLeave={
          sidebarNavMode === "flyout" ? handleFlyoutLeave : undefined
        }
      >
        <div className={active ? "nav-links__active-border" : ""}></div>
        <Button
          className={`nav-item ${active ? "nav-links__active" : ""}`}
          variant="clear"
          title={sidebarNavMode === "flyout" ? undefined : item.name}
          aria-label={item.name}
          aria-current={active ? "page" : undefined}
          onClick={() => {
            handleNavigation(item);
          }}
          tabIndex={0}
        >
          <span className="nav-item__icon">
            {item.lucideIcon ? (
              <item.lucideIcon
                className={`w-5 h-5 stroke-[1.5] ${active ? "text-[#974ae4]" : "text-gray-600"}`}
              />
            ) : item.icon ? (
              <Icon
                style={{
                  height: "20",
                  width: "20",
                }}
                name={item.icon as any}
              />
            ) : null}
          </span>
          {!isMobile && (
            <span
              className={`nav-item__label ${active ? "text-[#974ae4]" : "text-gray-600"}`}
            >
              {item.name}
            </span>
          )}
        </Button>
        {isMobile && <p className="nav-label">{item.name}</p>}
      </div>
    );
  };

  const sidebarClass = `custom-sidebar ${isMobile ? "mobile" : "desktop"} ${
    isPinnedOpen ? "overlay-open" : ""
  } ${isPinned ? "pinned" : ""} ${
    sidebarNavMode === "flyout" ? "flyout-mode" : ""
  }`;

  return (
    <>
      {isMobile && popoverOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setPopoverOpen(false)}
          aria-hidden="true"
        />
      )}
      <nav
        ref={sidebarRef}
        className={sidebarClass}
        role="navigation"
        aria-label="Sidebar Navigation"
        onMouseEnter={() => {
          if (!isMobile && sidebarNavMode === "hover" && hoverEnabled)
            setIsHovering(true);
        }}
        onMouseLeave={() => {
          if (!isMobile && sidebarNavMode === "hover" && hoverEnabled)
            setIsHovering(false);
        }}
      >
        {!isMobile && (
          <>
            {isPinnedOpen ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "18px 12px 14px 16px",
                  gap: 10,
                  flexShrink: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{ width: 44, height: 44, borderRadius: 9 }}
                  className="flex items-center justify-center bg-white flex-shrink-0"
                >
                  <img src={logo} alt="Logo" className="w-9 h-9" />
                </div>
                <div className="flex flex-col min-w-0 flex-1 gap-[2px]">
                  <span className="text-[18px] font-bold text-gray-900 leading-tight truncate">
                    ExecBridge
                  </span>
                  {organizationName && (
                    <span className="text-[14px] text-gray-500 truncate capitalize leading-tight">
                      {organizationName}
                    </span>
                  )}
                </div>
                <button
                  onClick={handlePinToggle}
                  onMouseEnter={e =>
                    showTooltip(
                      isPinned ? "Unpin sidebar" : "Pin sidebar",
                      e.currentTarget
                    )
                  }
                  onMouseLeave={hideTooltip}
                  aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
                  aria-pressed={isPinned}
                  tabIndex={0}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "background-color 0.2s ease",
                  }}
                  className={
                    isPinned
                      ? "bg-[#974ae4] text-white hover:bg-[#7a36c4]"
                      : "text-gray-600 hover:bg-[#EDEDED]"
                  }
                >
                  <Pin
                    className={`w-4 h-4 stroke-[1.5] ${isPinned ? "fill-white" : ""}`}
                  />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 pt-4 pb-3 border-b border-gray-200 w-full">
                <div
                  style={{ width: 42, height: 42, borderRadius: 9 }}
                  className="flex items-center justify-center flex-shrink-0"
                >
                  <img src={logo} alt="Logo" className="w-9 h-9" />
                </div>
                {sidebarNavMode === "hover" && !hoverEnabled && (
                  <button
                    onClick={handlePinToggle}
                    aria-label="Pin sidebar"
                    title="Pin sidebar"
                    tabIndex={0}
                    className="sidebar-logo-pin flex items-center justify-center text-gray-600 hover:bg-[#EDEDED] hover:text-gray-900"
                  >
                    <Pin className="w-3.5 h-3.5 stroke-[1.5]" />
                  </button>
                )}
              </div>
            )}
          </>
        )}

        <div className="nav-links">
          {visibleItems.map(renderNavItem)}

          {isMobile && overflowItems.length > 0 && (
            <div className="nav-links__container" key="more-icon">
              <Button
                id="moreIcon"
                variant="clear"
                className="nav-item"
                onClick={togglePopover}
                aria-label="More options"
                aria-haspopup="true"
                aria-expanded={popoverOpen}
                tabIndex={0}
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </Button>
              <p className="nav-label">More</p>
            </div>
          )}
        </div>

        {!isMobile && (
          <div
            className={`profile-icon-container ${isPinnedOpen ? "flex flex-col gap-2" : "flex flex-col gap-3 items-center"}`}
            ref={profileContainerRef}
          >
            {isPinnedOpen ? (
              // Expanded view: Profile card with name, role, notifications, settings, and sign out
              <>
                <div className="flex items-center gap-3 p-3.5 bg-purple-50 border border-purple-100 w-full shadow-sm">
                  <div className="flex-shrink-0">
                    {renderProfileAvatar("w-9 h-9")}
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate leading-tight">
                      {fullName || "User"}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {roleDisplayName}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-purple-100 rounded-lg transition-all duration-200"
                      onClick={() => {
                        setNotificationPanelOpen(true);
                      }}
                      aria-label="Notifications"
                      tabIndex={0}
                    >
                      <Bell className="w-5 h-5 stroke-[1.5]" />
                      {unread > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-semibold border-2 border-purple-50 shadow-sm">
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                    </button>
                    <button
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-purple-100 rounded-lg transition-all duration-200"
                      onClick={() => setIsProfileSettingsOpen(true)}
                      aria-label="Settings"
                      title="Profile Settings"
                      tabIndex={0}
                    >
                      <Settings className="w-5 h-5 stroke-[1.5]" />
                    </button>
                  </div>
                </div>
                <button
                  className="flex items-center gap-2.5 text-sm font-medium text-gray-700 hover:text-red-600 transition-all duration-200 w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 mt-1"
                  onClick={handleLogout}
                  aria-label="Sign Out"
                  tabIndex={0}
                  disabled={logoutLoading}
                >
                  <LogOut className="w-5 h-5 stroke-[1.5]" />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              // Collapsed view: Notification bell, logout icon, and profile avatar.
              // Full-width wrappers match nav-links__container so flyout labels
              // dock to the sidebar edge (leaving the same gap as nav pills).
              <>
                <div
                  className="sidebar-footer-action__row"
                  onMouseEnter={
                    sidebarNavMode === "flyout"
                      ? e => handleFlyoutEnter("Notifications", e.currentTarget)
                      : undefined
                  }
                  onMouseLeave={
                    sidebarNavMode === "flyout" ? handleFlyoutLeave : undefined
                  }
                >
                  <button
                    type="button"
                    className={`sidebar-footer-action sidebar-footer-action--notifications relative text-gray-600 ${
                      sidebarNavMode === "flyout"
                        ? ""
                        : "hover:text-gray-900 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      hideFlyoutLabel();
                      setNotificationPanelOpen(true);
                    }}
                    aria-label="Notifications"
                    tabIndex={0}
                  >
                    <Bell className="w-5 h-5 stroke-[1.5]" />
                    {unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-semibold border-2 border-white">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </button>
                </div>
                <div
                  className="sidebar-footer-action__row"
                  onMouseEnter={
                    sidebarNavMode === "flyout"
                      ? e =>
                          handleFlyoutEnter("Sign-Out", e.currentTarget, {
                            variant: "danger",
                          })
                      : undefined
                  }
                  onMouseLeave={
                    sidebarNavMode === "flyout" ? handleFlyoutLeave : undefined
                  }
                >
                  <button
                    type="button"
                    className={`sidebar-footer-action sidebar-footer-action--signout text-gray-600 ${
                      sidebarNavMode === "flyout"
                        ? ""
                        : "hover:text-red-600 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      hideFlyoutLabel();
                      handleLogout();
                    }}
                    aria-label="Sign Out"
                    tabIndex={0}
                    disabled={logoutLoading}
                  >
                    <LogOut className="w-5 h-5 stroke-[1.5]" />
                  </button>
                </div>
                <div
                  className="sidebar-footer-action__row"
                  onMouseEnter={
                    sidebarNavMode === "flyout"
                      ? e =>
                          handleFlyoutEnter(
                            fullName || "User",
                            e.currentTarget,
                            {
                              variant: "profile",
                              subtitle: roleDisplayName,
                            }
                          )
                      : undefined
                  }
                  onMouseLeave={
                    sidebarNavMode === "flyout" ? handleFlyoutLeave : undefined
                  }
                >
                  <button
                    type="button"
                    className="sidebar-footer-action sidebar-footer-action--profile"
                    onClick={() => {
                      hideFlyoutLabel();
                      setIsProfileSettingsOpen(true);
                    }}
                    aria-label="Profile Settings"
                    tabIndex={0}
                  >
                    {renderProfileAvatar("w-8 h-8")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {isMobile && overflowItems.length > 0 && popoverOpen && (
          <div className="more-popover absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[60] min-w-[180px] max-w-[220px]">
            <div
              className="more-items"
              role="menu"
              aria-label="More navigation items"
            >
              {overflowItems.map(item => {
                const active = isNavItemActive(item.name);
                return (
                  <button
                    key={item.name}
                    className={`more-item w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? "bg-purple-50 text-purple-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    aria-label={item.name}
                    onClick={() => {
                      handleNavigation(item);
                      setPopoverOpen(false);
                    }}
                    tabIndex={0}
                  >
                    {item.lucideIcon ? (
                      <item.lucideIcon
                        className={`w-5 h-5 stroke-[1.5] flex-shrink-0 ${
                          active ? "text-purple-600" : "text-gray-600"
                        }`}
                      />
                    ) : item.icon ? (
                      <Icon
                        style={{ height: "20", width: "20" }}
                        name={item.icon as any}
                      />
                    ) : null}
                    <span className="text-sm font-medium flex-1">
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <NotificationPanel
          isOpen={notificationPanelOpen}
          onClose={() => setNotificationPanelOpen(false)}
        />
        <ProfileSettingsModal
          isOpen={isProfileSettingsOpen}
          toggle={() => setIsProfileSettingsOpen(false)}
        />
      </nav>
      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x,
            top: tooltip.y,
            transform: "translateY(-50%)",
            zIndex: 9999,
            backgroundColor: "#1f2937",
            color: "#ffffff",
            fontSize: 12,
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            padding: "5px 9px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          }}
        >
          {tooltip.text}
        </div>
      )}
      {flyoutLabel && (
        <div
          className={`sidebar-flyout-label${
            flyoutLabel.variant === "danger"
              ? " sidebar-flyout-label--danger"
              : flyoutLabel.variant === "profile"
                ? " sidebar-flyout-label--profile"
                : ""
          }`}
          style={{
            position: "fixed",
            left: flyoutLabel.x,
            top: flyoutLabel.y,
            zIndex: 9999,
          }}
        >
          {flyoutLabel.variant === "profile" ? (
            <>
              <span className="sidebar-flyout-label__title">
                {flyoutLabel.text}
              </span>
              {flyoutLabel.subtitle && (
                <span className="sidebar-flyout-label__subtitle">
                  {flyoutLabel.subtitle}
                </span>
              )}
            </>
          ) : (
            flyoutLabel.text
          )}
        </div>
      )}
    </>
  );
};

export default Sidebar;
